import { ChangeDetectionStrategy, Component, effect, inject, Input, signal } from '@angular/core';
import { SideSheetFormComponent } from '../../../organisms/side-sheet-form/side-sheet-form.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerComponent } from '../../../atoms/date-picker/date-picker.component';
import { SideSheetContentDirective } from '../../../organisms/side-sheet-form/directives/side-sheet-content.directive';
import {
  getContractAddressText,
  getNonExpiredContracts,
  getSendMasterDataChangeRequestActionState,
  getSendMasterDataChangeRequestResponse,
  ibanValidator,
  IMasterdataChangeRequest,
  MasterdataChangeType,
  nameValidator,
  SendMasterDataChangeRequest,
} from '@resident-nx/shared';
import { ImmomioAnchorComponent } from '@immomio/ui/button';
import { MultiSelectDropdownV2WebComponent } from '../../multi-select-dropdown-v2/multi-select-dropdown-v2.component';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { NgbActiveOffcanvas, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { PaymentChangeRequestResponseComponent } from '../payment-change-request-response/payment-change-request-response.component';
import { SideSheetService } from '../../../../services/general/side-sheet.service';
import { UserProfileEditSummaryComponent } from '../../user-profile/user-profile-edit/user-profile-edit-summary/user-profile-edit-summary.component';
import { DatePipe } from '@angular/common';
import { InfoModalWebComponent } from '../../info-modal/info-modal.component';
import { ModalService } from '../../modal/modal.service';
import { ImmomioFormFieldComponent } from '@immomio/ui/form';

@Component({
  standalone: true,
  selector: 'rs-web-payment-information-edit',
  templateUrl: './payment-information-edit.component.html',
  styleUrl: './payment-information-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SideSheetFormComponent,
    TranslateModule,
    ReactiveFormsModule,
    DatePickerComponent,
    SideSheetContentDirective,
    ImmomioAnchorComponent,
    MultiSelectDropdownV2WebComponent,
    UserProfileEditSummaryComponent,
    DatePipe,
    ImmomioFormFieldComponent,
  ],
})
export class PaymentInformationEditComponent {
  #ngbActiveOffcanvas = inject(NgbActiveOffcanvas);
  #sideSheetService = inject(SideSheetService);
  #fb = inject(FormBuilder);
  #store = inject(Store);
  #modal = inject(ModalService);
  @Input() notice: string | undefined;
  readonly contractSelections = toSignal(
    this.#store.select(getNonExpiredContracts).pipe(
      map(contracts =>
        contracts.map(contract => ({
          id: contract.id,
          text: getContractAddressText(contract),
        }))
      )
    )
  );
  readonly changeRequestActionState = this.#store.selectSignal(
    getSendMasterDataChangeRequestActionState
  );
  readonly response = this.#store.selectSignal(getSendMasterDataChangeRequestResponse);
  readonly allContractsSelected = signal(false);
  readonly minDate: NgbDateStruct = (() => {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
  })();
  readonly form = this.#fb.group({
    iban: ['', [Validators.required, ibanValidator()]],
    accountOwner: ['', [Validators.required, nameValidator()]],
    issuedAt: [
      `${this.minDate.year}-${this.minDate.month.toString().padStart(2, '0')}-${this.minDate.day.toString().padStart(2, '0')}`,
      [Validators.required],
    ],
    selectedContracts: [[] as { id: string; text: string }[], [Validators.required]],
    hasAcceptedSepaMandate: false,
  });

  responseEff = effect(() => {
    const response = this.response();
    if (response) {
      this.#sideSheetService.open(PaymentChangeRequestResponseComponent);
      this.#ngbActiveOffcanvas.close();
    }
  });

  public allContractsSelectedToggle(): void {
    this.allContractsSelected.update(selected => !selected);
    const selectedContracts = this.allContractsSelected() ? this.contractSelections() : [];

    this.form.controls.selectedContracts.patchValue(selectedContracts);
  }

  public sendRequest(): void {
    const { selectedContracts, hasAcceptedSepaMandate, issuedAt, ...payment } =
      this.form.getRawValue();
    const contractIds = selectedContracts.map(contract => contract.id);
    const payload: IMasterdataChangeRequest = {
      type: MasterdataChangeType.PAYMENT,
      contractIds,
      payment: {
        ...payment,
        issuedAt: new Date(issuedAt).toISOString(),
        hasAcceptedSepaMandate,
        notice: hasAcceptedSepaMandate ? this.notice : null,
      },
    };
    this.#store.dispatch(SendMasterDataChangeRequest({ payload }));
  }

  public openNotice() {
    this.#modal.open(InfoModalWebComponent, {
      centered: true,
      data: {
        title: 'user_payment_edit.form_direct_debit_concent_toggle_link',
        text: this.notice,
      },
    });
  }

  protected readonly MasterDataChangeType = MasterdataChangeType;
}
